import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
 ChevronRight,
 ChevronDown,
 Sparkles,
 ArrowLeft,
 Loader2,
 AlertCircle,
 Plus,
 Trash2,
 Save,
 Gauge,
 Tags,
 HelpCircle,
 Boxes,
 GitBranch,
 FlaskConical,
} from 'lucide-react';
import {
 lldCustomProblemService,
 CustomLLDProblemInput,
 CustomLLDClarification,
 CustomLLDExpectedEntity,
 CustomLLDExpectedRelationship,
 CustomLLDTestCase,
 CustomLLDDifficulty,
 CustomLLDCategory,
} from '../../../services/lldCustomProblemService';
import {
 validateCustomProblem,
 CUSTOM_PROBLEM_DIFFICULTIES,
 CUSTOM_PROBLEM_CATEGORIES,
 CUSTOM_PROBLEM_LIMITS,
} from '../../../utils/lldValidation';

interface CustomProblemFormProps {
 mode: 'create' | 'edit';
}

// ─── Local editable shape (collections always defined for the editor) ─────────────

interface FormState {
 title: string;
 description: string;
 difficulty: string;
 category: string;
 clarifications: CustomLLDClarification[];
 expectedEntities: CustomLLDExpectedEntity[];
 expectedRelationships: CustomLLDExpectedRelationship[];
 testCases: CustomLLDTestCase[];
}

const EMPTY_FORM: FormState = {
 title: '',
 description: '',
 difficulty: '',
 category: '',
 clarifications: [],
 expectedEntities: [],
 expectedRelationships: [],
 testCases: [],
};

// ─── Collapsible optional section card ────────────────────────────────────────────

const CollapsibleSection: React.FC<{
 title: string;
 subtitle: string;
 icon: React.ElementType;
 count?: number;
 defaultOpen?: boolean;
 children: React.ReactNode;
}> = ({ title, subtitle, icon: Icon, count, defaultOpen = false, children }) => {
 const [open, setOpen] = useState(defaultOpen);
 return (
  <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden">
   <button
    type="button"
    onClick={() => setOpen((v) => !v)}
    className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
    aria-expanded={open}
   >
    <div className="flex items-center gap-3 min-w-0">
     <span className="w-9 h-9 rounded-xl bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-300 flex items-center justify-center flex-shrink-0">
      <Icon className="w-4 h-4" />
     </span>
     <div className="min-w-0">
      <div className="flex items-center gap-2">
       <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{title}</h3>
       {typeof count === 'number' && count > 0 && (
        <span className="text-[11px] font-medium px-1.5 py-0.5 rounded-full bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300 tabular-nums">
         {count}
        </span>
       )}
      </div>
      <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5 truncate">{subtitle}</p>
     </div>
    </div>
    <ChevronDown
     className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
    />
   </button>
   {open && <div className="px-5 pb-5 pt-1 border-t border-gray-100 dark:border-gray-800">{children}</div>}
  </div>
 );
};

const fieldClass =
 'w-full px-3.5 py-2.5 text-sm rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 placeholder-gray-500 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-400 dark:focus:border-primary-500 transition-shadow';

const addBtnClass =
 'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-primary-200 dark:border-primary-800 bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 hover:bg-primary-100 dark:hover:bg-primary-900/50 transition-colors';

const CustomProblemForm: React.FC<CustomProblemFormProps> = ({ mode }) => {
 const navigate = useNavigate();
 const { id } = useParams<{ id: string }>();

 const [form, setForm] = useState<FormState>(EMPTY_FORM);
 const [loading, setLoading] = useState(mode === 'edit');
 const [loadError, setLoadError] = useState<string | null>(null);
 const [submitting, setSubmitting] = useState(false);
 const [serverError, setServerError] = useState<string | null>(null);
 const [showErrors, setShowErrors] = useState(false);

 // ─── Edit mode: prefill via getById ───────────────────────────────────────────
 useEffect(() => {
  if (mode !== 'edit' || !id) return;
  let cancelled = false;
  const load = async () => {
   setLoading(true);
   setLoadError(null);
   try {
    const p = await lldCustomProblemService.getById(id);
    if (cancelled) return;
    setForm({
     title: p.title ?? '',
     description: p.description ?? '',
     difficulty: p.difficulty ?? '',
     category: p.category ?? '',
     clarifications: p.clarifications ?? [],
     expectedEntities: p.expectedEntities ?? [],
     expectedRelationships: p.expectedRelationships ?? [],
     testCases: p.testCases ?? [],
    });
   } catch (err: any) {
    if (!cancelled) setLoadError(err?.message || 'Failed to load this problem.');
   } finally {
    if (!cancelled) setLoading(false);
   }
  };
  load();
  return () => {
   cancelled = true;
  };
 }, [mode, id]);

 // ─── Validation (mirrors the server DTO) ──────────────────────────────────────
 const validation = useMemo(
  () =>
   validateCustomProblem({
    title: form.title,
    description: form.description,
    difficulty: form.difficulty || undefined,
    category: form.category || undefined,
    clarifications: form.clarifications,
    expectedEntities: form.expectedEntities,
    expectedRelationships: form.expectedRelationships,
    testCases: form.testCases,
   }),
  [form]
 );

 const err = (field: string): string | undefined =>
  showErrors ? validation.errors[field] : undefined;

 // ─── Generic patch helper ─────────────────────────────────────────────────────
 const patch = useCallback((updates: Partial<FormState>) => {
  setForm((prev) => ({ ...prev, ...updates }));
 }, []);

 // ─── Build the API payload (drop empty optionals) ─────────────────────────────
 const buildPayload = (): CustomLLDProblemInput => {
  const payload: CustomLLDProblemInput = {
   title: form.title,
   description: form.description,
  };
  if (form.difficulty) payload.difficulty = form.difficulty as CustomLLDDifficulty;
  if (form.category) payload.category = form.category as CustomLLDCategory;
  if (form.clarifications.length) payload.clarifications = form.clarifications;
  if (form.expectedEntities.length) payload.expectedEntities = form.expectedEntities;
  if (form.expectedRelationships.length) payload.expectedRelationships = form.expectedRelationships;
  if (form.testCases.length) payload.testCases = form.testCases;
  return payload;
 };

 const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  setServerError(null);
  if (!validation.valid) {
   setShowErrors(true);
   return;
  }
  setSubmitting(true);
  try {
   if (mode === 'edit' && id) {
    await lldCustomProblemService.update(id, buildPayload());
   } else {
    await lldCustomProblemService.create(buildPayload());
   }
   navigate('/practice/lld');
  } catch (err: any) {
   // Surface server-side 400 validation messages (may be a string or string[])
   const data = err?.data;
   let message = err?.message || 'Failed to save the problem. Please try again.';
   if (data && Array.isArray(data.message)) {
    message = data.message.join('\n');
   } else if (data && typeof data.message === 'string') {
    message = data.message;
   }
   setServerError(message);
   setSubmitting(false);
  }
 };

 // ─── Loading / load-error states (edit mode) ──────────────────────────────────
 if (loading) {
  return (
   <div className="min-h-screen bg-gray-50 dark:bg-gray-950 pt-16">
    <div className="flex flex-col items-center justify-center py-24">
     <Loader2 className="w-8 h-8 text-primary-500 animate-spin mb-3" />
     <p className="text-gray-600 dark:text-gray-400 text-sm">Loading problem…</p>
    </div>
   </div>
  );
 }

 if (loadError) {
  return (
   <div className="min-h-screen bg-gray-50 dark:bg-gray-950 pt-16">
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
     <div className="flex flex-col items-center justify-center py-20 text-center">
      <AlertCircle className="w-9 h-9 text-rose-500 mb-3" />
      <p className="text-gray-600 dark:text-gray-400 mb-4">{loadError}</p>
      <button
       onClick={() => navigate('/practice/lld')}
       className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium"
      >
       Back to catalog
      </button>
     </div>
    </div>
   </div>
  );
 }

 return (
  <div className="lld-page lld-form-page min-h-screen bg-gray-50 dark:bg-gray-950 pt-16">
   <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
    {/* Breadcrumb */}
    <nav className="flex items-center text-sm text-gray-500 dark:text-gray-500 mb-6">
     <Link to="/practice" className="hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
      Practice
     </Link>
     <ChevronRight className="w-4 h-4 mx-1.5" />
     <Link to="/practice/lld" className="hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
      LLD Practice
     </Link>
     <ChevronRight className="w-4 h-4 mx-1.5" />
     <span className="text-gray-700 dark:text-gray-300 font-medium">
      {mode === 'edit' ? 'Edit problem' : 'New problem'}
     </span>
    </nav>

    {/* Header */}
    <div className="relative overflow-hidden rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 sm:p-7 mb-6">
     <div className="pointer-events-none absolute -top-24 -right-16 w-72 h-72 rounded-full bg-primary-500/5 dark:bg-primary-500/10 blur-3xl" />
     <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary-400/40 to-transparent" />
     <div className="relative">
      <div className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-primary-600 dark:text-primary-400 mb-2">
       <Sparkles className="w-3.5 h-3.5" /> Your problem
      </div>
      <h1 className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">
       {mode === 'edit' ? 'Edit custom problem' : 'Create a custom problem'}
      </h1>
      <p className="mt-1.5 text-gray-600 dark:text-gray-400 text-sm max-w-xl leading-relaxed">
       A title and description are all you need to get started. Add structure — entities,
       relationships, test scenarios — whenever you want.
      </p>
     </div>
    </div>

    {/* Server error banner */}
    {serverError && (
     <div className="mb-6 rounded-xl border border-rose-200 dark:border-rose-900/50 bg-rose-50 dark:bg-rose-900/20 px-4 py-3 flex items-start gap-3">
      <AlertCircle className="w-5 h-5 text-rose-500 flex-shrink-0 mt-0.5" />
      <div className="min-w-0">
       <p className="text-sm font-medium text-rose-700 dark:text-rose-300">Couldn’t save</p>
       <p className="text-sm text-rose-600 dark:text-rose-400 whitespace-pre-wrap mt-0.5">{serverError}</p>
      </div>
     </div>
    )}

    <form onSubmit={handleSubmit} className="space-y-5">
     {/* ─── Required block ──────────────────────────────────────────────── */}
     <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 sm:p-6 space-y-5">
      {/* Title */}
      <div>
       <div className="flex items-center justify-between mb-1.5">
        <label htmlFor="cp-title" className="text-sm font-medium text-gray-700 dark:text-gray-300">
         Title <span className="text-rose-500">*</span>
        </label>
        <span
         className={`text-[11px] tabular-nums ${form.title.length > CUSTOM_PROBLEM_LIMITS.titleMax
          ? 'text-rose-500 font-medium'
          : 'text-gray-500 dark:text-gray-500'
          }`}
        >
         {form.title.length}/{CUSTOM_PROBLEM_LIMITS.titleMax}
        </span>
       </div>
       <input
        id="cp-title"
        type="text"
        value={form.title}
        onChange={(e) => patch({ title: e.target.value })}
        placeholder="e.g. Design a movie ticket booking system"
        className={fieldClass}
       />
       {err('title') && <p className="mt-1.5 text-xs text-rose-500">{err('title')}</p>}
      </div>

      {/* Description */}
      <div>
       <div className="flex items-center justify-between mb-1.5">
        <label htmlFor="cp-description" className="text-sm font-medium text-gray-700 dark:text-gray-300">
         Description <span className="text-rose-500">*</span>
        </label>
        <span
         className={`text-[11px] tabular-nums ${form.description.length > CUSTOM_PROBLEM_LIMITS.descriptionMax
          ? 'text-rose-500 font-medium'
          : 'text-gray-500 dark:text-gray-500'
          }`}
        >
         {form.description.length}/{CUSTOM_PROBLEM_LIMITS.descriptionMax}
        </span>
       </div>
       <textarea
        id="cp-description"
        value={form.description}
        onChange={(e) => patch({ description: e.target.value })}
        rows={6}
        placeholder="Describe the problem, the core requirements, and any constraints…"
        className={`${fieldClass} resize-y leading-relaxed`}
       />
       {err('description') && <p className="mt-1.5 text-xs text-rose-500">{err('description')}</p>}
      </div>
     </div>

     {/* ─── Optional: Difficulty & Category ─────────────────────────────── */}
     <CollapsibleSection
      title="Difficulty"
      subtitle="Optional — help yourself gauge the challenge"
      icon={Gauge}
      count={form.difficulty ? 1 : 0}
     >
      <div className="mt-3">
       <select
        value={form.difficulty}
        onChange={(e) => patch({ difficulty: e.target.value })}
        className={fieldClass}
       >
        <option value="">No difficulty</option>
        {CUSTOM_PROBLEM_DIFFICULTIES.map((d) => (
         <option key={d} value={d}>
          {d}
         </option>
        ))}
       </select>
       {err('difficulty') && <p className="mt-1.5 text-xs text-rose-500">{err('difficulty')}</p>}
      </div>
     </CollapsibleSection>

     <CollapsibleSection
      title="Category"
      subtitle="Optional — group the problem by theme"
      icon={Tags}
      count={form.category ? 1 : 0}
     >
      <div className="mt-3">
       <select
        value={form.category}
        onChange={(e) => patch({ category: e.target.value })}
        className={fieldClass}
       >
        <option value="">No category</option>
        {CUSTOM_PROBLEM_CATEGORIES.map((c) => (
         <option key={c} value={c}>
          {c}
         </option>
        ))}
       </select>
       {err('category') && <p className="mt-1.5 text-xs text-rose-500">{err('category')}</p>}
      </div>
     </CollapsibleSection>

     {/* ─── Optional: Clarifications ────────────────────────────────────── */}
     <CollapsibleSection
      title="Clarifications"
      subtitle="Optional — sample questions and the answers you'd give"
      icon={HelpCircle}
      count={form.clarifications.length}
     >
      <div className="mt-3 space-y-3">
       {form.clarifications.map((c, i) => (
        <div key={i} className="rounded-xl border border-gray-200 dark:border-gray-800 p-3 space-y-2">
         <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Q&A {i + 1}</span>
          <button
           type="button"
           onClick={() =>
            patch({ clarifications: form.clarifications.filter((_, idx) => idx !== i) })
           }
           className="p-1 text-gray-400 hover:text-rose-500 transition-colors"
           aria-label="Remove clarification"
          >
           <Trash2 className="w-4 h-4" />
          </button>
         </div>
         <input
          type="text"
          value={c.question}
          onChange={(e) => {
           const next = [...form.clarifications];
           next[i] = { ...next[i], question: e.target.value };
           patch({ clarifications: next });
          }}
          placeholder="Question"
          className={fieldClass}
         />
         <input
          type="text"
          value={c.answer}
          onChange={(e) => {
           const next = [...form.clarifications];
           next[i] = { ...next[i], answer: e.target.value };
           patch({ clarifications: next });
          }}
          placeholder="Answer"
          className={fieldClass}
         />
        </div>
       ))}
       <button
        type="button"
        onClick={() =>
         patch({ clarifications: [...form.clarifications, { question: '', answer: '' }] })
        }
        className={addBtnClass}
       >
        <Plus className="w-3.5 h-3.5" /> Add clarification
       </button>
      </div>
     </CollapsibleSection>

     {/* ─── Optional: Expected entities ─────────────────────────────────── */}
     <CollapsibleSection
      title="Expected entities"
      subtitle={`Optional — up to ${CUSTOM_PROBLEM_LIMITS.expectedEntitiesMax}`}
      icon={Boxes}
      count={form.expectedEntities.length}
     >
      <div className="mt-3 space-y-3">
       {form.expectedEntities.map((ent, i) => (
        <div key={i} className="rounded-xl border border-gray-200 dark:border-gray-800 p-3 space-y-2">
         <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Entity {i + 1}</span>
          <button
           type="button"
           onClick={() =>
            patch({ expectedEntities: form.expectedEntities.filter((_, idx) => idx !== i) })
           }
           className="p-1 text-gray-400 hover:text-rose-500 transition-colors"
           aria-label="Remove entity"
          >
           <Trash2 className="w-4 h-4" />
          </button>
         </div>
         <input
          type="text"
          value={ent.name}
          onChange={(e) => {
           const next = [...form.expectedEntities];
           next[i] = { ...next[i], name: e.target.value };
           patch({ expectedEntities: next });
          }}
          placeholder="Entity name (e.g. Booking)"
          className={fieldClass}
         />
         <input
          type="text"
          value={ent.description}
          onChange={(e) => {
           const next = [...form.expectedEntities];
           next[i] = { ...next[i], description: e.target.value };
           patch({ expectedEntities: next });
          }}
          placeholder="What this entity represents"
          className={fieldClass}
         />
        </div>
       ))}
       {form.expectedEntities.length < CUSTOM_PROBLEM_LIMITS.expectedEntitiesMax && (
        <button
         type="button"
         onClick={() =>
          patch({
           expectedEntities: [
            ...form.expectedEntities,
            { name: '', description: '', attributes: [], methods: [] },
           ],
          })
         }
         className={addBtnClass}
        >
         <Plus className="w-3.5 h-3.5" /> Add entity
        </button>
       )}
       {err('expectedEntities') && (
        <p className="mt-1.5 text-xs text-rose-500">{err('expectedEntities')}</p>
       )}
      </div>
     </CollapsibleSection>

     {/* ─── Optional: Expected relationships ────────────────────────────── */}
     <CollapsibleSection
      title="Expected relationships"
      subtitle={`Optional — up to ${CUSTOM_PROBLEM_LIMITS.expectedRelationshipsMax}`}
      icon={GitBranch}
      count={form.expectedRelationships.length}
     >
      <div className="mt-3 space-y-3">
       {form.expectedRelationships.map((rel, i) => (
        <div key={i} className="rounded-xl border border-gray-200 dark:border-gray-800 p-3 space-y-2">
         <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
           Relationship {i + 1}
          </span>
          <button
           type="button"
           onClick={() =>
            patch({
             expectedRelationships: form.expectedRelationships.filter((_, idx) => idx !== i),
            })
           }
           className="p-1 text-gray-400 hover:text-rose-500 transition-colors"
           aria-label="Remove relationship"
          >
           <Trash2 className="w-4 h-4" />
          </button>
         </div>
         <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <input
           type="text"
           value={rel.source}
           onChange={(e) => {
            const next = [...form.expectedRelationships];
            next[i] = { ...next[i], source: e.target.value };
            patch({ expectedRelationships: next });
           }}
           placeholder="Source"
           className={fieldClass}
          />
          <input
           type="text"
           value={rel.target}
           onChange={(e) => {
            const next = [...form.expectedRelationships];
            next[i] = { ...next[i], target: e.target.value };
            patch({ expectedRelationships: next });
           }}
           placeholder="Target"
           className={fieldClass}
          />
          <input
           type="text"
           value={rel.type}
           onChange={(e) => {
            const next = [...form.expectedRelationships];
            next[i] = { ...next[i], type: e.target.value };
            patch({ expectedRelationships: next });
           }}
           placeholder="Type (e.g. composition)"
           className={fieldClass}
          />
          <input
           type="text"
           value={rel.cardinality}
           onChange={(e) => {
            const next = [...form.expectedRelationships];
            next[i] = { ...next[i], cardinality: e.target.value };
            patch({ expectedRelationships: next });
           }}
           placeholder="Cardinality (e.g. 1-to-many)"
           className={fieldClass}
          />
         </div>
        </div>
       ))}
       {form.expectedRelationships.length < CUSTOM_PROBLEM_LIMITS.expectedRelationshipsMax && (
        <button
         type="button"
         onClick={() =>
          patch({
           expectedRelationships: [
            ...form.expectedRelationships,
            { source: '', target: '', type: '', cardinality: '' },
           ],
          })
         }
         className={addBtnClass}
        >
         <Plus className="w-3.5 h-3.5" /> Add relationship
        </button>
       )}
       {err('expectedRelationships') && (
        <p className="mt-1.5 text-xs text-rose-500">{err('expectedRelationships')}</p>
       )}
      </div>
     </CollapsibleSection>

     {/* ─── Optional: Test scenarios ────────────────────────────────────── */}
     <CollapsibleSection
      title="Test scenarios"
      subtitle={`Optional — up to ${CUSTOM_PROBLEM_LIMITS.testCasesMax}`}
      icon={FlaskConical}
      count={form.testCases.length}
     >
      <div className="mt-3 space-y-3">
       {form.testCases.map((tc, i) => (
        <div key={i} className="rounded-xl border border-gray-200 dark:border-gray-800 p-3 space-y-2">
         <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Scenario {i + 1}</span>
          <button
           type="button"
           onClick={() => patch({ testCases: form.testCases.filter((_, idx) => idx !== i) })}
           className="p-1 text-gray-400 hover:text-rose-500 transition-colors"
           aria-label="Remove scenario"
          >
           <Trash2 className="w-4 h-4" />
          </button>
         </div>
         <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <input
           type="text"
           value={tc.name}
           onChange={(e) => {
            const next = [...form.testCases];
            next[i] = { ...next[i], name: e.target.value };
            patch({ testCases: next });
           }}
           placeholder="Scenario name"
           className={fieldClass}
          />
          <select
           value={tc.category}
           onChange={(e) => {
            const next = [...form.testCases];
            next[i] = { ...next[i], category: e.target.value as CustomLLDTestCase['category'] };
            patch({ testCases: next });
           }}
           className={fieldClass}
          >
           <option value="functional">functional</option>
           <option value="edge-case">edge-case</option>
          </select>
         </div>
         <input
          type="text"
          value={tc.input}
          onChange={(e) => {
           const next = [...form.testCases];
           next[i] = { ...next[i], input: e.target.value };
           patch({ testCases: next });
          }}
          placeholder="Input"
          className={fieldClass}
         />
         <input
          type="text"
          value={tc.expectedOutput}
          onChange={(e) => {
           const next = [...form.testCases];
           next[i] = { ...next[i], expectedOutput: e.target.value };
           patch({ testCases: next });
          }}
          placeholder="Expected output"
          className={fieldClass}
         />
         <input
          type="text"
          value={tc.assertion}
          onChange={(e) => {
           const next = [...form.testCases];
           next[i] = { ...next[i], assertion: e.target.value };
           patch({ testCases: next });
          }}
          placeholder="Assertion / what to verify"
          className={fieldClass}
         />
        </div>
       ))}
       {form.testCases.length < CUSTOM_PROBLEM_LIMITS.testCasesMax && (
        <button
         type="button"
         onClick={() =>
          patch({
           testCases: [
            ...form.testCases,
            { name: '', category: 'functional', input: '', expectedOutput: '', assertion: '' },
           ],
          })
         }
         className={addBtnClass}
        >
         <Plus className="w-3.5 h-3.5" /> Add scenario
        </button>
       )}
       {err('testCases') && <p className="mt-1.5 text-xs text-rose-500">{err('testCases')}</p>}
      </div>
     </CollapsibleSection>

     {/* ─── Actions ─────────────────────────────────────────────────────── */}
     <div className="flex items-center justify-between gap-3 pt-2">
      <button
       type="button"
       onClick={() => navigate('/practice/lld')}
       className="inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium rounded-xl text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
      >
       <ArrowLeft className="w-4 h-4" /> Cancel
      </button>
      <button
       type="submit"
       disabled={submitting || !validation.valid}
       className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-xl bg-primary-600 hover:bg-primary-700 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
      >
       {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
       {mode === 'edit' ? 'Save changes' : 'Create problem'}
      </button>
     </div>
     {!validation.valid && showErrors && (
      <p className="text-right text-xs text-rose-500">
       Fix the highlighted fields before saving.
      </p>
     )}
    </form>
   </div>
  </div>
 );
};

export default CustomProblemForm;
